import { useCallback, useEffect, useState } from 'react';
import { matterApi, type MatterEvent } from '../../api';

type Props = { matterId: number | string };

const EVENT_LABELS: Record<string, { label: string; color: string }> = {
  created:           { label: 'Matter created',      color: 'var(--color-success)' },
  stage_changed:     { label: 'Stage advanced',      color: 'var(--color-accent)' },
  party_added:       { label: 'Party added',         color: 'var(--color-info)' },
  party_withdrawn:   { label: 'Party withdrawn',     color: 'var(--color-text-muted)' },
  party_substituted: { label: 'Party substituted',   color: 'var(--color-info)' },
  note:              { label: 'Note',                color: 'var(--color-text-muted)' },
  document_added:    { label: 'Document added',      color: 'var(--color-info)' },
  task_completed:    { label: 'Task completed',      color: 'var(--color-success)' },
  invoice_issued:    { label: 'Invoice issued',      color: 'var(--color-accent)' },
  finalised:         { label: 'Matter finalised',    color: 'var(--color-success)' },
};

export default function EventsPanel({ matterId }: Props) {
  const [events, setEvents] = useState<MatterEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [posting, setPosting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await matterApi.events(matterId);
      setEvents(r.events || []);
    } finally { setLoading(false); }
  }, [matterId]);

  useEffect(() => { load(); }, [load]);

  const addNote = async () => {
    if (!note.trim()) return;
    setPosting(true);
    try {
      // Posts to /matters/:id/notes
      await fetch(`/server/api/matters/${matterId}/notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Token': localStorage.getItem('litigate_token') || '',
          'X-Tenant-Id': localStorage.getItem('litigate.tenantId') || '',
        },
        body: JSON.stringify({ body: note }),
      });
      setNote('');
      load();
    } finally { setPosting(false); }
  };

  return (
    <div className="stack">
      <div className="row">
        <h3 className="grow" style={{ margin: 0 }}>Events</h3>
      </div>

      <div className="card">
        <div className="stack">
          <label style={{ fontSize: 11 }}>Add a note</label>
          <textarea
            rows={2}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Short update, attendance summary, or reminder for the file…"
          />
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button className="primary" onClick={addNote} disabled={posting || !note.trim()}>
              {posting ? 'Saving…' : 'Add note'}
            </button>
          </div>
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 18 }} className="muted">Loading…</div>
        ) : events.length === 0 ? (
          <div style={{ padding: 18 }} className="muted">
            No events yet. Matter creation, party changes, stage advancement, and notes will appear here.
          </div>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {events.map((e) => {
              const meta = EVENT_LABELS[e.event_type] || { label: e.event_type, color: 'var(--color-text-muted)' };
              return (
                <li key={String(e.ROWID)} style={row}>
                  <div style={{
                    width: 8, height: 8, borderRadius: 999, background: meta.color, flexShrink: 0,
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 500 }}>{e.summary}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {meta.label} · {formatDT(e.occurred_at)}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function formatDT(iso: string) {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
}

const row: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 14,
  padding: '12px 18px', borderBottom: '1px solid var(--color-border)',
};
